// @ts-check
import { gql } from '@apollo/client/core';
import { Image } from '@react-pdf/renderer';
import React from 'react';
import { Box, Page, Registration } from '../../../pdf-design-system';
import { PagesProvider } from '../../../pdf-design-system/Base/PageLayout/Page';
import { ThemeProvider } from '../../../pdf-design-system/useTheme';
import UserLocaleProvider from '../../../Providers/UserLocaleProvider';
import getStands from '../getStands';
import messages from '../locale/messages';
// @ts-ignore images are not supported by TS
import useRoom, { RoomProvider } from '../useRoom';
import VieVinumLine from '../components/VieVinumLine';
import VieVinumMyListCover from './VieVinumMyListCover';
import VieVinumMyListRearCover from './VieVinumMyListRearCover';
import VieVinumMyListTable, { theme } from './VieVinumMyListTable';

export default function VieVinumMyList({ data, locale, user, ...rest }) {
  const stands = afterQuery(data, rest);
  return (
    <PagesProvider>
      <UserLocaleProvider locale={locale} messages={messages}>
        <VieVinumMyListCover data={data} user={user} />
        {Array.from(Array(Math.ceil(stands.length / 2))).map((_, index) => (
          <SinglePage
            key={index}
            stands={stands.slice(index * 2, index * 2 + 2)}
          />
        ))}
        <VieVinumMyListRearCover />
      </UserLocaleProvider>
    </PagesProvider>
  );
}

/** @param {{ stands: StandOrFile[]}} props */
function SinglePage({ stands }) {
  return (
    <Page size="A4" theme={{}}>
      {/* <Image
        src={`/${selfServiceCatalog}`}
        fixed
        style={{
          position: 'absolute',
          opacity: 0.3,
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
        }}
      /> */}
      <RoomProvider value={getRoom(stands[stands.length - 1])}>
        <StaticMyListPageContent>
          {stands.map((stand, index) => {
            if (stand.__typename === 'RegistrationCatalogSection')
              return (
                <Registration.Provider key={stand.key} value={getStand(stand)}>
                  <RoomWithThemeProvider room={getRoom(stand)}>
                    <VieVinumMyListTable />
                  </RoomWithThemeProvider>
                </Registration.Provider>
              );
            return <React.Fragment key={index} />;
          })}
        </StaticMyListPageContent>
      </RoomProvider>
    </Page>
  );
}

/**
 * @param {StandOrFile} section
 */
function getRoom(section) {
  /** @type {import('../helpers/rooms').Room | undefined} */
  return {
    name: '(Saal noch nicht bekannt)',
    colors: { primary: '#2dafe6' },
    ...getStand(section)?.registration?.stand?.room,
  };
}

function RoomWithThemeProvider({ room, children }) {
  return (
    <RoomProvider value={room}>
      <ThemeProvider
        theme={{ ...theme, colors: { ...theme.colors, ...room.colors } }}
      >
        {children}
      </ThemeProvider>
    </RoomProvider>
  );
}

export function StaticMyListPageContent({ children }) {
  const room = useRoom();
  return (
    <RoomWithThemeProvider room={room}>
      <VieVinumFooter
        fixed
        style={[
          { position: 'absolute', bottom: '7mm', left: '15mm', right: '15mm' },
        ]}
      />
      {children}
      <Separator />
    </RoomWithThemeProvider>
  );
}

export function Separator() {
  return (
    <Box
      style={{
        borderTopWidth: 0.5,
        borderTopStyle: 'dotted',
        borderColor: '#aaaaaa',
        position: 'absolute',
        top: '148.5mm',
        left: 0,
        right: 0,
      }}
    />
  );
}

function VieVinumFooter({ style, ...rest }) {
  return (
    <Box
      style={[
        { display: 'flex', flexDirection: 'row', alignItems: 'flex-end' },
        style,
      ]}
      {...rest}
    >
      <Image
        src="https://res.cloudinary.com/bottlebooks/image/upload/c_fill,h_170,q_auto,w_240/vievinum/vievinum.jpg"
        style={{ width: '10mm', marginVertical: '-0.3mm' }}
      />
      <VieVinumLine style={{ width: '169mm', marginLeft: '1mm' }} />
    </Box>
  );
}

/** @typedef {ReturnType<getStand>} Stand */
/** @param {StandOrFile} section */
function getStand(section) {
  if (section.__typename === 'RegistrationCatalogSection') {
    return section;
  }
  return undefined;
}

/** @type {import('@apollo/client').TypedDocumentNode<import('./@types/VieVinumMyList').VieVinumMyListFragment>}*/
VieVinumMyList.fragment = gql`
  fragment VieVinumMyList on CatalogConfiguration {
    catalogConfigurationId
    sections(filter: { registration: { companyId: { in: $companyIds } } }) {
      __typename
      ... on RegistrationCatalogSection {
        registration {
          registrationId
          companyId
          isLocked
        }
        ...VieVinum_getStands
      }
      ... on UploadedFileCatalogSection {
        filename
      }
      ...VieVinumMyListTable
    }
    ...VieVinumMyListCover
  }

  ${getStands.fragment}
  ${VieVinumMyListTable.fragment}
  ${VieVinumMyListCover.fragment}
`;

/** @typedef {ReturnType<afterQuery>[number]} StandOrFile */
/**
 * @param {import('./@types/VieVinumMyList').VieVinumMyListFragment} data
 * @param {{ companyId?: string; registrationId?: string; }} rest
 */
function afterQuery(data, rest) {
  const sections = getSections(data, rest);
  const stands = sections
    .map((section) => {
      if (section.__typename === 'UploadedFileCatalogSection') return section;
      return getStands(section, section.registration.registeredProducts.nodes);
    })
    .flat();

  return stands;
}

/** @typedef {ReturnType<getSections>[number]} Section */
/**
 * @param {import('./@types/VieVinumMyList').VieVinumMyListFragment} data
 * @param {{ companyId?: string; registrationId?: string; isLocked?: string }} params
 */
function getSections(data, params) {
  if (params.companyId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.companyId === params.companyId
    );
  }
  if (params.registrationId) {
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.registrationId === params.registrationId
    );
  }
  if (params.isLocked != null) {
    const isLocked = params.isLocked === 'true' ? true : false;
    return data.sections?.filter(
      (section) =>
        section.__typename === 'RegistrationCatalogSection' &&
        section.registration.isLocked === isLocked
    );
  }
  return data.sections;
}
